/* eslint-disable react-hooks/rules-of-hooks */
"use client";

import type React from "react";

import {
  Button,
  Input,
  HStack,
  VStack,
  Box,
  Text,
  Spinner,
  SimpleGrid,
  IconButton,
  Tooltip,
  Textarea,
} from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { BoxTitle } from "@/components/BoxTitle";
import { ModalAlert } from "@/components/ModalStyled/Modal";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useFetchItem, useSave } from "../serviceVeiculos";
import ModalRetorno from "@/components/components/ModalRetorno";

import { FaUpload, FaArrowLeft } from "react-icons/fa";

import { FormValues, formValuesToResource, resourceToFormValues, schema } from "../modelVeiculos";



type ModalMode = "form" | "import";

const ModalVeiculo: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const [modalMode, setModalMode] = useState<ModalMode>("form");
  const [isRetornoOpen, setIsRetornoOpen] = useState(false);
  const [mensagemRetorno, setMensagemRetorno] = useState("");
  const [tipoRetorno, setTipoRetorno] = useState<"sucesso" | "erro">("sucesso");

  const {
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      demanda: 0,
      descricao: "",
      obs: "",
    },
  });

  const { data, isLoading } = id
    ? useFetchItem(Number(id))
    : { data: undefined, isLoading: false };

  useEffect(() => {
    if (id && data) {
      reset(resourceToFormValues(data));
    }
  }, [id, data, reset]);

  const save = useSave(id);

  const onSubmit = async (formData: FormValues) => {
    const dataToSend = formValuesToResource(formData);

    try {
      await save.mutateAsync(dataToSend);
      setMensagemRetorno(
        isEdit ? "Depósito atualizado com sucesso!" : "Depósito criado com sucesso!"
      );
      setTipoRetorno("sucesso");
      setIsRetornoOpen(true);
    } catch (error) {
      setMensagemRetorno(
        "Erro ao salvar depósito. " + ((error as any)?.message || "")
      );
      setTipoRetorno("erro");
      setIsRetornoOpen(true);
    }
  };

  const getModalTitle = () => {
    if (isEdit) {
      return "Editar Comentario";
    }

    return "Novo Comentario"
  };

  const getModalSize = () => {
    return modalMode === "import" ? "4xl" : "3xl";
  };

  const renderHeader = () => (
    <HStack w={"100%"} justify="space-between" align="center">
      <HStack>
        {modalMode === "import" && !isEdit && (
          <Tooltip label="Voltar ao formulário">
            <IconButton
              aria-label="Voltar ao formulário"
              icon={<FaArrowLeft />}
              size="sm"
              variant="ghost"
              onClick={() => setModalMode("form")}
            />
          </Tooltip>
        )}
        <Text fontSize="xl" ml={2}>
          {getModalTitle()}
        </Text>
      </HStack>

      <HStack>
        {!isEdit && modalMode === "form" && (
          <Tooltip
            backgroundColor={"#117BAA"}
            borderRadius={"25px"}
            label="Importar em lote"
          >
            <IconButton
              aria-label="Importar em lote"
              icon={<FaUpload />}
              size="sm"
              backgroundColor={"#117BAA"}
              variant="outline"
              onClick={() => setModalMode("import")}
            />
          </Tooltip>
        )}
      </HStack>
    </HStack>
  );

  const renderBody = () => {

    return (
      <>
        {isEdit && isLoading ? (
          <Box textAlign="center">
            <Spinner />
          </Box>
        ) : (
          <Box m={2} as="form" onSubmit={handleSubmit(onSubmit)}>
            <VStack spacing={6} align="stretch">
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                <Box>
                  <BoxTitle title="Demanda">
                    <Input
                      type="number"
                      value={watch("demanda") || 0}
                      onChange={(e) => {
                        setValue("demanda", Number(e.target.value));
                      }}
                      isInvalid={!!errors.demanda}
                    />
                  </BoxTitle>
                     {errors.demanda && (
                      <Text color="red.500" fontSize="sm" mt={1}>
                        {errors.demanda.message}
                      </Text>
                    )}
                </Box>
                <Box>
                  <BoxTitle title="Descrição">
                    <Input
                      value={watch("descricao") || ""}
                      onChange={(e) => setValue("descricao", e.target.value)}
                      placeholder="Digite a descrição"
                      isInvalid={!!errors.descricao}
                    />
                  </BoxTitle>
                    {errors.descricao && (
                      <Text color="red.500" fontSize="sm" mt={1}>
                        {errors.descricao.message}
                      </Text>
                    )}
                </Box>
              </SimpleGrid>
              <HStack spacing={2}>
                <Box flex={1}>
                  <BoxTitle title="Observação">
                    <Textarea
                      value={watch("obs") || ""}
                      onChange={(e) => setValue("obs", e.target.value)}
                      placeholder="Digite a observação"
                      rows={4}
                      isInvalid={!!errors.obs}
                    />
                  </BoxTitle>
                   {errors.obs && (
                      <Text color="red.500" fontSize="sm" mt={1}>
                        {errors.obs.message}
                      </Text>
                    )}
                </Box>
              </HStack>
            </VStack>
          </Box>
        )}
      </>
    );
  };

  const renderFooter = () => {
    if (modalMode === "import") {
      return <></>;
    }

    return (
      <Button
        backgroundColor={"#117baa"}
        onClick={handleSubmit(onSubmit)}
        isLoading={save.isLoading}
        color={"white"}
      >
        {isEdit ? "Salvar Alterações" : "Confirmar"}
      </Button>
    );
  };

  return (
    <>
      <ModalAlert
        isOpen={true}
        xIsback
        size={getModalSize()}
        title={renderHeader()}
        body={renderBody()}
        footer={renderFooter()}
      />

      <ModalRetorno
        isOpen={isRetornoOpen}
        onClose={() => {
          setIsRetornoOpen(false);
          if (tipoRetorno === "sucesso") {
            if (modalMode === "import") {
              setModalMode("form");
            } else {
              navigate(-1);
            }
          }
        }}
        mensagem={mensagemRetorno}
        tipo={tipoRetorno}
      />
    </>
  );
};

export default ModalVeiculo;